import { useCallback, useContext, useEffect, useState } from 'react';
import {
    JiraBugIcon,
    JiraEpicIcon,
    JiraStoryIcon,
    JiraSubtaskIcon,
    JiraTaskIcon,
} from '../../../assets/svg';
import { FaArrowDown } from 'react-icons/fa';
import Button from '../../../components/shared/button';
import { getWritePermission } from '../../../helpers/getPermission';
import { OrganisationsContext } from '../../../context/OrganisationsContext';
import moment from 'moment';
import Loader from '../../../components/loader';
import { useFetchDataAndSetState } from '../../../helpers/useFetchDataAndSetState';
import { createTicket, deleteTicket, getTickets } from '../../../api/settings-slo/slo';
import { useTranslation } from 'react-i18next';
import ErrorIndicator from '../../../components/error-indicator';
import showNotification from '../../../helpers/showNotification';
import ModalWrapper from '../../../components/shared/modals/modal-wrapper';
import DeleteContent from '../../../components/shared/modals/delete-content';


function TicketsPanel({ serviceName }) {
    const { t } = useTranslation();
    const LIMIT = 3;
    const ticketsRequest = useCallback(
        () => getTickets(serviceName),
        [serviceName]
    );
    const [ticketsState, ticketsDispatch] = useFetchDataAndSetState(
        ticketsRequest,
        [serviceName]
    );
    const { organisationsState } = useContext(OrganisationsContext);
    const [ticketsData, setTicketsData] = useState(null);
    const [page, setPage] = useState(0);
    const [displayedTickets, setDisplayedTickets] = useState([]);
    const [isAddingTicket, setIsAddingTicket] = useState(false);
    const [newTicket, setNewTicket] = useState('');
    const [addingTicketError, setAddingTicketError] = useState(null);
    const [addingTicketLoading, setAddingTicketLoading] = useState(false);

    useEffect(() => {
        setDisplayedTickets(ticketsData?.slice(0, LIMIT));
        setPage(0);
    }, [ticketsData]);

    useEffect(() => {
        setDisplayedTickets(ticketsData?.slice(0, page * LIMIT + LIMIT));
    }, [page]);

    // Enanle setting tickets list from ticketsState
    useEffect(() => {
        setTicketsData(ticketsState?.data);
    }, [ticketsState?.data]);

    function handleLoadMoreTickets() {
        setPage((prevState) => prevState + 1);
    }

    async function handleAddingTicket() {
        if (!newTicket?.length) {
            setAddingTicketError('Ticket ID is required');
            return;
        }

        setAddingTicketLoading(true);

        try {
            const res = await createTicket(serviceName, { id: newTicket});

            if (res?.status === 200) {
                ticketsDispatch({ type: 'SET_DATA', payload: [...ticketsState?.data, res?.data] });
            }
            setIsAddingTicket(false);
        } catch (err) {
            setAddingTicketError(
                err.response?.data ||
                    `Ticket has not been added, please try again`
            );
        }
        setAddingTicketLoading(false);
    }

    async function handleDeleteTicket(ticketId) {
        try {
            const res = await deleteTicket(serviceName, ticketId);

            if (res?.status === 200) {
                ticketsDispatch({
                    type: 'SET_DATA',
                    payload: ticketsState?.data?.filter(
                        (ticket) => ticketId !== ticket.id
                    ),
                });
            }
        } catch (err) {
            showNotification(`Failed to delete ${ticketId}`);
        }
    }

    return (
        <div
            className={`w-96 min-h-48 flex-none flex flex-col overflow-y-auto `}
        >
            <p className="mb-1 font-display text-2xl font-light mb-[6px]  text-gray-500">
                {t('sre.actions')}
            </p>
            {ticketsState?.error ? (
                <ErrorIndicator error={ticketsState?.error} />
            ) : (
                <>
                    {ticketsState?.loading ? (
                        <div className="w-full h-full flex justify-center items-center">
                            <Loader
                                color={'#C2C7D7'}
                                size={36}
                                speedMultiplier={0.8}
                            />
                        </div>
                    ) : (
                        <div className={`shrink flex flex-col gap-2  `}>
                            {displayedTickets?.length ? (
                                displayedTickets?.map((ticket, i) => (
                                    <Ticket
                                        ticketData={ticket}
                                        key={ticket?.id + i}
                                        handleDeleteTicket={handleDeleteTicket}
                                    />
                                ))
                            ) : (
                                <p className="text-center text-gray-500 text-xl p-4">
                                    {t('common.no_data')}
                                </p>
                            )}
                            {ticketsData?.length > displayedTickets?.length ? (
                                <div className="w-full flex justify-center">
                                    {' '}
                                    <Button
                                        extraClasses={
                                            'p-2 text-sm flex items-center justify-center gap-1  text-gray-500'
                                        }
                                        onClick={() => handleLoadMoreTickets()}
                                    >
                                        {t('sre.more')}
                                        <FaArrowDown className="w-2 h-2 text-gray-500" />
                                    </Button>
                                </div>
                            ) : null}
                            {getWritePermission(organisationsState?.data) ? (
                                <>
                                    {isAddingTicket ? (
                                        <>
                                            <div className="w-full  flex gap-x-4">
                                                <input
                                                    id={moment().unix()}
                                                    value={newTicket}
                                                    type="text"
                                                    placeholder="Insert Jira ticket ID"
                                                    className="p-2 rounded border border-solid text-gray-500 border-gray-500 flex-grow ring-transparent	"
                                                    autoFocus={true}
                                                    onChange={(event) => {
                                                        setNewTicket(
                                                            event.target.value
                                                        );
                                                    }}
                                                />
                                                {addingTicketLoading ? (
                                                    <div className="w-12 h-full flex justify-center items-center">
                                                        <Loader
                                                            color={'#C2C7D7'}
                                                            size={24}
                                                            speedMultiplier={
                                                                0.8
                                                            }
                                                        />
                                                    </div>
                                                ) : (
                                                    <Button
                                                        type="button"
                                                        variant="whiteHoverBlue"
                                                        onClick={() =>
                                                            handleAddingTicket()
                                                        }
                                                    >
                                                        {t('button.add')}
                                                    </Button>
                                                )}
                                            </div>
                                            {addingTicketError ? (
                                                <small className="text-xs text-red-600 text-left ">
                                                    {addingTicketError}
                                                </small>
                                            ) : null}
                                        </>
                                    ) : (
                                        <Button
                                            type="button"
                                            extraClasses="w-full p-2 rounded border border-solid border-gray-500 text-gray-500"
                                            onClick={() =>
                                                setIsAddingTicket(true)
                                            }
                                        >
                                            {t('sre.add_ticket')}
                                        </Button>
                                    )}
                                </>
                            ) : null}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default TicketsPanel;

function Ticket({ ticketData, handleDeleteTicket }) {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);

    function renderTypeIcon(type) {
        switch (type) {
            case 'bug':
                return <JiraBugIcon />;
            case 'story':
                return <JiraStoryIcon />;
            case 'epic':
                return <JiraEpicIcon />;
            case 'subtask':
                return <JiraSubtaskIcon />;
            case 'task':
            default:
                return <JiraTaskIcon />;
        }
    }

    function getStatusClasses(status) {
        switch (status) {
            case 'done':
            case 'closed':
                return 'bg-green-500';
            case 'in progress':
            case 'review':
                return 'bg-blue-400';
            case 'selected for development':
            case 'backlog':
            default:
                return 'bg-gray-100';
        }
    }
    return (
        <div className=" flex-none rounded border border-solid border-gray-200 p-2 h-24 flex flex-col justify-between">
            <p>{ticketData?.title}</p>
            <div>
                <div className="flex justify-between">
                    <div className="flex gap-1 align-center">
                        {renderTypeIcon(ticketData?.type)}
                        <p className="text-gray-500 text-sm">
                            {ticketData?.id}
                        </p>
                    </div>
                    <p
                        className={`rounded  px-1 uppercase text-sm ${getStatusClasses(
                            ticketData?.status
                        )}`}
                    >
                        {ticketData?.status}
                    </p>
                </div>
                <div className="flex items-center justify-between mt-1">
                    <a
                        href={ticketData?.url}
                        className="block text-xs text-theme-tertiary underline "
                        target="_blank"
                    >
                        Open in Jira
                    </a>
                    <Button
                        variant="redText"
                        extraClasses="text-xs font-sans"
                        onClick={() => setIsModalOpen(true)}
                    >
                        {t('button.remove')}
                    </Button>
                </div>
            </div>
            <ModalWrapper
                modalState={isModalOpen}
                setModalState={setIsModalOpen}
                title={`Remove the action`}
                height={200}
            >
                <DeleteContent
                    handleAction={() => handleDeleteTicket(ticketData?.id)}
                    setModalState={setIsModalOpen}
                >
                    <p className="text-xl text-gray-500">
                        {`Remove ${ticketData?.id} from SLO from the service actions?`}
                    </p>
                </DeleteContent>
            </ModalWrapper>
        </div>
    );
}
